import { useEffect, useMemo, useState } from 'react';
import { GlassCard, Pill } from '../components/ui';
import { PremiumCard, PremiumPage, ToggleTile } from '../components/premium';
import { NavIcon } from '../components/NavIcons';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import type { UserProfile } from '../types';
import { cmToFtIn, ftInToCm, kgToLbs, lbsToKg } from '../lib/units';
import { WEEKDAY_KEYS, WEEKDAY_LABELS, type WeekdayKey } from '../lib/weeklyCalories';
import { trackingFieldForModule } from '../lib/trackingPrefs';
import { hasAdFreeExperience } from '../lib/coachAccess';
import { AdFreeUpgradeCard } from '../components/AdFreeUpgradeCard';
import { btnPrimary } from '../lib/uiClasses';
import { getTrainingPrefs, setTrainingPrefs, WEEKDAY_LABELS_SHORT } from '../lib/trainingPrefs';
import { isNativeIOS } from '../lib/platform';
import { formatHealthSyncSuccess, isAppleHealthAuthorized, syncAppleHealth } from '../lib/appleHealth';

const activities = [
  { value: 'sedentary', label: 'Sedentary' },
  { value: 'light', label: 'Light' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'active', label: 'Active' },
  { value: 'very_active', label: 'Very active' },
];

const goalModes = [
  { value: 'cut', label: 'Cut' },
  { value: 'bulk', label: 'Bulk' },
  { value: 'maintenance', label: 'Maintenance' },
];

export function ProfilePage() {
  const { user, refresh, setUserFields } = useAuth();
  const unitPref: 'metric' | 'imperial' =
    user?.preferredUnitSystem === 'imperial' ? 'imperial' : 'metric';

  const [p, setP] = useState<UserProfile | null>(null);
  const [form, setForm] = useState({
    age: '30',
    sex: 'male',
    heightCm: '178',
    heightFt: '5',
    heightIn: '10',
    weightKg: '80',
    weightLbs: '176',
    bodyFatEstimate: '',
    activityLevel: 'moderate',
    fitnessGoal: 'maintenance',
  });
  const [unitChoice, setUnitChoice] = useState<'metric' | 'imperial'>(unitPref);
  const [manualTargets, setManualTargets] = useState(false);
  const [calorieStr, setCalorieStr] = useState('');
  const [proteinStr, setProteinStr] = useState('');
  const [useWeekly, setUseWeekly] = useState(false);
  const [weeklyInputs, setWeeklyInputs] = useState<Record<WeekdayKey, string>>({
    sun: '',
    mon: '',
    tue: '',
    wed: '',
    thu: '',
    fri: '',
    sat: '',
  });
  const [trackFiber, setTrackFiber] = useState(false);
  const [trackSugar, setTrackSugar] = useState(false);
  const [trackSodium, setTrackSodium] = useState(false);
  const [trackNutrition, setTrackNutrition] = useState(true);
  const [trackWorkouts, setTrackWorkouts] = useState(true);
  const [trackSleep, setTrackSleep] = useState(true);
  const [trackGoals, setTrackGoals] = useState(true);
  const [circlesEnabled, setCirclesEnabled] = useState(false);
  const [circlesBusy, setCirclesBusy] = useState(false);
  const [leaderboardsOptIn, setLeaderboardsOptIn] = useState(false);
  const [leaderboardsBusy, setLeaderboardsBusy] = useState(false);
  const [healthBusy, setHealthBusy] = useState(false);
  const [healthSynced, setHealthSynced] = useState(() => isAppleHealthAuthorized());
  const [trackingBusy, setTrackingBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [nutrientPrefBusy, setNutrientPrefBusy] = useState(false);
  const [trainingPrefs, setTrainingPrefsState] = useState(() => getTrainingPrefs(user?.id));

  useEffect(() => {
    setUnitChoice((c) => (c === unitPref ? c : unitPref));
  }, [unitPref]);

  useEffect(() => {
    if (!user) return;
    setTrackNutrition(user.trackNutrition !== false);
    setTrackWorkouts(user.trackWorkouts !== false);
    setTrackSleep(user.trackSleep !== false);
    setTrackGoals(user.trackGoals !== false);
    setCirclesEnabled(Boolean(user.circlesEnabled));
    setLeaderboardsOptIn(Boolean(user.leaderboardsOptIn));
  }, [user]);

  useEffect(() => {
    (async () => {
      const profile = await api<UserProfile | null>('/api/profile');
      if (profile) {
        setTrackFiber(!!profile.trackFiber);
        setTrackSugar(!!profile.trackSugar);
        setTrackSodium(!!profile.trackSodium);
      } else {
        setTrackFiber(false);
        setTrackSugar(false);
        setTrackSodium(false);
      }
      if (profile && profile.heightCm != null && profile.weightKg != null) {
        setP(profile);
        const { feet, inches } = cmToFtIn(profile.heightCm);
        setForm({
          age: String(profile.age ?? ''),
          sex: profile.sex ?? 'male',
          heightCm: String(profile.heightCm),
          heightFt: String(feet),
          heightIn: String(inches),
          weightKg: String(profile.weightKg),
          weightLbs: String(Math.round(kgToLbs(profile.weightKg) * 10) / 10),
          bodyFatEstimate: profile.bodyFatEstimate != null ? String(profile.bodyFatEstimate) : '',
          activityLevel: profile.activityLevel,
          fitnessGoal: profile.fitnessGoal,
        });
        setManualTargets(!!profile.manualNutritionTargets);
        setCalorieStr(profile.calorieTarget != null ? String(profile.calorieTarget) : '');
        setProteinStr(profile.proteinTargetG != null ? String(profile.proteinTargetG) : '');
        setUseWeekly(!!profile.useWeeklyCalorieTargets);
        const wk: Record<WeekdayKey, string> = {
          sun: '',
          mon: '',
          tue: '',
          wed: '',
          thu: '',
          fri: '',
          sat: '',
        };
        const raw = profile.weeklyCalorieTargets;
        if (raw && typeof raw === 'object') {
          for (const k of WEEKDAY_KEYS) {
            const v = (raw as Record<string, unknown>)[k];
            if (v != null && v !== '') wk[k] = String(v);
          }
        }
        setWeeklyInputs(wk);
      }
    })();
  }, [unitPref]);

  async function saveCirclesEnabled(next: boolean) {
    const prev = circlesEnabled;
    setCirclesEnabled(next);
    setCirclesBusy(true);
    setMsg(null);
    try {
      const updated = await api<{ circlesEnabled: boolean }>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ circlesEnabled: next }),
      });
      const enabled = Boolean(updated.circlesEnabled);
      setCirclesEnabled(enabled);
      setUserFields({ circlesEnabled: enabled });
      setMsg(enabled ? 'Circles enabled.' : 'Circles disabled.');
    } catch {
      setCirclesEnabled(prev);
      setMsg('Could not update Circles preference.');
    } finally {
      setCirclesBusy(false);
    }
  }

  async function saveLeaderboardsOptIn(next: boolean) {
    const prev = leaderboardsOptIn;
    setLeaderboardsOptIn(next);
    setLeaderboardsBusy(true);
    setMsg(null);
    try {
      const updated = await api<{ leaderboardsOptIn: boolean }>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ leaderboardsOptIn: next }),
      });
      const enabled = Boolean(updated.leaderboardsOptIn);
      setLeaderboardsOptIn(enabled);
      setUserFields({ leaderboardsOptIn: enabled });
      setMsg(enabled ? 'You are on leaderboards.' : 'Leaderboards opt-out saved.');
    } catch {
      setLeaderboardsOptIn(prev);
      setMsg('Could not update leaderboard preference.');
    } finally {
      setLeaderboardsBusy(false);
    }
  }

  async function saveTrackingPrefs(patch: Partial<{
    trackNutrition: boolean;
    trackWorkouts: boolean;
    trackSleep: boolean;
    trackGoals: boolean;
  }>) {
    setTrackingBusy(true);
    setMsg(null);
    try {
      const updated = await api<{
        trackNutrition: boolean;
        trackWorkouts: boolean;
        trackSleep: boolean;
        trackGoals: boolean;
      }>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({
          trackNutrition: patch.trackNutrition ?? trackNutrition,
          trackWorkouts: patch.trackWorkouts ?? trackWorkouts,
          trackSleep: patch.trackSleep ?? trackSleep,
          trackGoals: patch.trackGoals ?? trackGoals,
        }),
      });
      setTrackNutrition(updated.trackNutrition !== false);
      setTrackWorkouts(updated.trackWorkouts !== false);
      setTrackSleep(updated.trackSleep !== false);
      setTrackGoals(updated.trackGoals !== false);
      setUserFields({
        trackNutrition: updated.trackNutrition !== false,
        trackWorkouts: updated.trackWorkouts !== false,
        trackSleep: updated.trackSleep !== false,
        trackGoals: updated.trackGoals !== false,
      });
      await refresh();
      setMsg('Tracking preferences saved.');
    } catch {
      setMsg('Could not save tracking preferences.');
    } finally {
      setTrackingBusy(false);
    }
  }

  async function saveNutrientPrefs(patch: Partial<{ trackFiber: boolean; trackSugar: boolean; trackSodium: boolean }>) {
    setNutrientPrefBusy(true);
    setMsg(null);
    try {
      const prof = await api<UserProfile>('/api/profile', {
        method: 'PATCH',
        body: JSON.stringify({
          trackFiber: patch.trackFiber ?? trackFiber,
          trackSugar: patch.trackSugar ?? trackSugar,
          trackSodium: patch.trackSodium ?? trackSodium,
        }),
      });
      setP(prof);
      setTrackFiber(!!prof.trackFiber);
      setTrackSugar(!!prof.trackSugar);
      setTrackSodium(!!prof.trackSodium);
      setMsg('Nutrition preference saved.');
    } catch {
      setMsg('Could not save preference. Try again or use Save profile below.');
    } finally {
      setNutrientPrefBusy(false);
    }
  }

  function chooseUnit(next: 'metric' | 'imperial') {
    if (next === unitChoice) return;
    setUnitChoice(next);
  }

  async function save() {
    setMsg(null);
    const heightCm =
      unitChoice === 'metric'
        ? Number(form.heightCm) || 170
        : ftInToCm(Number(form.heightFt) || 5, Number(form.heightIn) || 9);
    const weightKg =
      unitChoice === 'metric'
        ? Number(form.weightKg) || 70
        : lbsToKg(Number(form.weightLbs) || 150);

    const weeklyCalorieTargets: Record<string, number> = {};
    for (const k of WEEKDAY_KEYS) {
      const s = weeklyInputs[k].trim();
      if (s) weeklyCalorieTargets[k] = Math.round(Number(s));
    }

    const [profileRes] = await Promise.all([
      api<UserProfile>('/api/profile', {
        method: 'PUT',
        body: JSON.stringify({
          age: Number(form.age) || undefined,
          sex: form.sex,
          heightCm,
          weightKg,
          bodyFatEstimate: form.bodyFatEstimate ? Number(form.bodyFatEstimate) : undefined,
          activityLevel: form.activityLevel,
          fitnessGoal: form.fitnessGoal,
          manualNutritionTargets: manualTargets,
          ...(manualTargets
            ? { calorieTarget: calorieStr.trim() ? Number(calorieStr) : undefined, proteinTargetG: proteinStr.trim() ? Number(proteinStr) : undefined }
            : {}),
          useWeeklyCalorieTargets: useWeekly,
          weeklyCalorieTargets,
          trackFiber,
          trackSugar,
          trackSodium,
        }),
      }),
      api('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ preferredUnitSystem: unitChoice }),
      }),
    ]);
    setP(profileRes);
    setTrackFiber(!!profileRes.trackFiber);
    setTrackSugar(!!profileRes.trackSugar);
    setTrackSodium(!!profileRes.trackSodium);
    setUserFields({ preferredUnitSystem: unitChoice });
    await refresh();
    setMsg('Saved.');
  }

  const weeklyPreview = useMemo(() => {
    if (!useWeekly) return null;
    return WEEKDAY_KEYS.map((k) => ({
      k,
      label: WEEKDAY_LABELS[k],
      v: weeklyInputs[k].trim(),
    })).filter((x) => x.v);
  }, [useWeekly, weeklyInputs]);

  return (
    <PremiumPage title="Profile">

      <PremiumCard variant="hero">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent">
            <NavIcon name="profile" className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <div className="truncate font-display text-lg font-bold text-white">
              {user?.displayName || user?.email?.split('@')[0] || 'You'}
            </div>
            {user?.email ? <div className="truncate text-sm text-zinc-400">{user.email}</div> : null}
            {p ? (
              <div className="mt-1 text-xs text-zinc-500">
                {p.calorieTarget ?? '—'} kcal · {p.proteinTargetG ?? '—'}g protein
              </div>
            ) : null}
          </div>
        </div>
      </PremiumCard>

      {msg ? <div className="text-sm text-zinc-400">{msg}</div> : null}

      {!hasAdFreeExperience(user) ? (
        <GlassCard className="mb-0">
          <AdFreeUpgradeCard user={user} />
        </GlassCard>
      ) : null}

      <PremiumCard>
        <div className="text-sm font-semibold text-white">App sections</div>
        <p className="mt-1 text-xs text-zinc-500">What shows in nav and home. Coach is always on.</p>
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(
            [
              ['nutrition', trackNutrition, 'nutrition' as const, 'Nutrition'],
              ['workouts', trackWorkouts, 'workouts' as const, 'Train'],
              ['sleep', trackSleep, 'sleep' as const, 'Sleep'],
              ['goals', trackGoals, 'progress' as const, 'Goals'],
            ] as const
          ).map(([key, on, icon, label]) => {
            const field = trackingFieldForModule(key);
            return (
              <ToggleTile
                key={key}
                icon={<NavIcon name={icon} className="h-5 w-5" />}
                label={label}
                checked={on}
                disabled={trackingBusy}
                onChange={(next) => void saveTrackingPrefs({ [field]: next })}
              />
            );
          })}
        </div>
      </PremiumCard>

      {isNativeIOS() ? (
        <PremiumCard>
          <div className="text-sm font-semibold text-white">Apple Health</div>
          <p className="mt-1 text-xs text-zinc-500">
            {healthSynced
              ? 'Steps, active calories, workouts, and weight sync from Apple Health.'
              : 'Sync steps, active calories, Apple Watch workouts, and weight from Apple Health. Accept the iOS permission sheet when prompted.'}
          </p>
          <button
            type="button"
            disabled={healthBusy || healthSynced}
            onClick={async () => {
              if (healthSynced) return;
              setHealthBusy(true);
              try {
                const summary = await syncAppleHealth({ force: true });
                setHealthSynced(true);
                setMsg(formatHealthSyncSuccess(summary));
              } catch (e) {
                setMsg(
                  e instanceof Error
                    ? e.message
                    : 'Could not sync Apple Health. Allow access in Settings → Health → Peak Performance IQ.',
                );
              } finally {
                setHealthBusy(false);
              }
            }}
            className={`${btnPrimary} mt-4 w-full ${
              healthSynced ? '!cursor-default !bg-emerald-500/20 !text-emerald-200 opacity-90' : ''
            }`}
          >
            {healthBusy ? 'Syncing…' : healthSynced ? 'Synced with Apple Health' : 'Sync Apple Health'}
          </button>
        </PremiumCard>
      ) : null}

      <PremiumCard>
        <div className="text-sm font-semibold text-white">Circles</div>
        <p className="mt-1 text-xs text-zinc-500">
          Train with friends in private groups built for accountability, not comparison.
        </p>
        <label
          className={`mt-4 flex w-full cursor-pointer items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 ${
            circlesBusy ? 'opacity-50' : ''
          }`}
        >
          <span className="text-sm font-medium text-zinc-200">Join Circles with friends</span>
          <input
            type="checkbox"
            role="switch"
            aria-checked={circlesEnabled}
            checked={circlesEnabled}
            disabled={circlesBusy}
            onChange={(e) => void saveCirclesEnabled(e.target.checked)}
            className="peer sr-only"
          />
          <span
            aria-hidden
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
              circlesEnabled ? 'bg-violet-500/80' : 'bg-zinc-700'
            }`}
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                circlesEnabled ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </span>
        </label>
      </PremiumCard>

      <PremiumCard>
        <div className="text-sm font-semibold text-white">Leaderboards</div>
        <p className="mt-1 text-xs text-zinc-500">
          Opt in to appear on individual and circle rankings by momentum score. Off by default.
        </p>
        <label
          className={`mt-4 flex w-full cursor-pointer items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 ${
            leaderboardsBusy ? 'opacity-50' : ''
          }`}
        >
          <span className="text-sm font-medium text-zinc-200">Show me on leaderboards</span>
          <input
            type="checkbox"
            role="switch"
            aria-checked={leaderboardsOptIn}
            checked={leaderboardsOptIn}
            disabled={leaderboardsBusy}
            onChange={(e) => void saveLeaderboardsOptIn(e.target.checked)}
            className="peer sr-only"
          />
          <span
            aria-hidden
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
              leaderboardsOptIn ? 'bg-amber-500/80' : 'bg-zinc-700'
            }`}
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                leaderboardsOptIn ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`}
            />
          </span>
        </label>
      </PremiumCard>

      {trackWorkouts ? (
        <PremiumCard>
          <div className="text-sm font-semibold text-white">Training preferences</div>
          <p className="mt-1 text-xs text-zinc-500">Schedule, rest timer, and logging options.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {WEEKDAY_LABELS_SHORT.map((label, i) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  const next = trainingPrefs.preferredWorkoutDays.includes(i)
                    ? trainingPrefs.preferredWorkoutDays.filter((d) => d !== i)
                    : [...trainingPrefs.preferredWorkoutDays, i].sort();
                  const saved = setTrainingPrefs(user?.id, { preferredWorkoutDays: next });
                  setTrainingPrefsState(saved);
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
                  trainingPrefs.preferredWorkoutDays.includes(i)
                    ? 'bg-sky-500/20 text-sky-200 ring-1 ring-sky-500/30'
                    : 'bg-white/[0.04] text-zinc-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <ToggleTile
              icon={<span className="text-xs font-bold">⏱</span>}
              label="Auto rest timer"
              checked={trainingPrefs.autoRestTimer}
              onChange={(next) => setTrainingPrefsState(setTrainingPrefs(user?.id, { autoRestTimer: next }))}
            />
            <ToggleTile
              icon={<span className="text-xs font-bold">RPE</span>}
              label="Default RPE"
              checked={trainingPrefs.defaultRpeTracking}
              onChange={(next) =>
                setTrainingPrefsState(setTrainingPrefs(user?.id, { defaultRpeTracking: next, advancedMetrics: next }))
              }
            />
          </div>
        </PremiumCard>
      ) : null}

      <PremiumCard>
        <div className="text-sm font-semibold text-white">Extra nutrients</div>
        <p className="mt-1 text-xs text-zinc-500">Optional fields on the Nutrition page.</p>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <ToggleTile
            icon={<span className="text-sm font-bold">F</span>}
            label="Fiber"
            checked={trackFiber}
            disabled={nutrientPrefBusy}
            onChange={(next) => void saveNutrientPrefs({ trackFiber: next })}
          />
          <ToggleTile
            icon={<span className="text-sm font-bold">S</span>}
            label="Sugar"
            checked={trackSugar}
            disabled={nutrientPrefBusy}
            onChange={(next) => void saveNutrientPrefs({ trackSugar: next })}
          />
          <ToggleTile
            icon={<span className="text-sm font-bold">Na</span>}
            label="Sodium"
            checked={trackSodium}
            disabled={nutrientPrefBusy}
            onChange={(next) => void saveNutrientPrefs({ trackSodium: next })}
          />
        </div>
      </PremiumCard>

      <div className="grid min-w-0 gap-4 lg:grid-cols-2">
        <GlassCard>
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm font-semibold text-white">Body &amp; lifestyle</div>
            <Pill>local</Pill>
          </div>
          <div className="mb-4 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => chooseUnit('metric')}
              className={`rounded-2xl border px-3 py-2 text-sm transition ${unitChoice === 'metric' ? 'border-accent/35 bg-accent/10 text-white' : 'border-white/10 text-zinc-400'}`}
            >
              Metric
            </button>
            <button
              type="button"
              onClick={() => chooseUnit('imperial')}
              className={`rounded-2xl border px-3 py-2 text-sm transition ${unitChoice === 'imperial' ? 'border-accent/35 bg-accent/10 text-white' : 'border-white/10 text-zinc-400'}`}
            >
              US
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-zinc-500">
              Age
              <input
                value={form.age}
                onChange={(e) => setForm((s) => ({ ...s, age: e.target.value }))}
                className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
              />
            </label>
            <label className="text-xs text-zinc-500">
              Sex
              <select
                value={form.sex}
                onChange={(e) => setForm((s) => ({ ...s, sex: e.target.value }))}
                className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </label>
            {unitChoice === 'metric' ? (
              <>
                <label className="text-xs text-zinc-500">
                  Height cm
                  <input value={form.heightCm} onChange={(e) => setForm((s) => ({ ...s, heightCm: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
                </label>
                <label className="text-xs text-zinc-500">
                  Weight kg
                  <input value={form.weightKg} onChange={(e) => setForm((s) => ({ ...s, weightKg: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
                </label>
              </>
            ) : (
              <>
                <label className="text-xs text-zinc-500">
                  Ft
                  <input value={form.heightFt} onChange={(e) => setForm((s) => ({ ...s, heightFt: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
                </label>
                <label className="text-xs text-zinc-500">
                  In
                  <input value={form.heightIn} onChange={(e) => setForm((s) => ({ ...s, heightIn: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
                </label>
                <label className="text-xs text-zinc-500 sm:col-span-2">
                  Weight lb
                  <input value={form.weightLbs} onChange={(e) => setForm((s) => ({ ...s, weightLbs: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
                </label>
              </>
            )}
            <label className="text-xs text-zinc-500">
              Body fat %
              <input value={form.bodyFatEstimate} onChange={(e) => setForm((s) => ({ ...s, bodyFatEstimate: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
            </label>
            <label className="text-xs text-zinc-500">
              Activity
              <select value={form.activityLevel} onChange={(e) => setForm((s) => ({ ...s, activityLevel: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white">
                {activities.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-zinc-500 sm:col-span-2">
              Goal (for formulas)
              <select value={form.fitnessGoal} onChange={(e) => setForm((s) => ({ ...s, fitnessGoal: e.target.value }))} className="mt-1 w-full rounded-2xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white">
                {goalModes.map((g) => (
                  <option key={g.value} value={g.value}>
                    {g.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </GlassCard>

        <GlassCard delay={0.05}>
          <div className="text-sm font-semibold text-white">Calories &amp; protein</div>
          <p className="mt-2 text-xs text-zinc-500">
            Maintenance: {p?.maintenanceCalories ?? '—'} kcal. Base target: {p?.calorieTarget ?? '—'}.
          </p>
          <label className="mt-4 flex cursor-pointer gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={manualTargets} onChange={(e) => setManualTargets(e.target.checked)} className="mt-0.5" />
            Custom daily calories &amp; protein (overrides formula)
          </label>
          {manualTargets ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <label className="text-xs text-zinc-500">
                Calories
                <input value={calorieStr} onChange={(e) => setCalorieStr(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
              </label>
              <label className="text-xs text-zinc-500">
                Protein g
                <input value={proteinStr} onChange={(e) => setProteinStr(e.target.value)} className="mt-1 w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white" />
              </label>
            </div>
          ) : null}

          <label className="mt-6 flex cursor-pointer gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={useWeekly} onChange={(e) => setUseWeekly(e.target.checked)} className="mt-0.5" />
            Different calorie target by day of week
          </label>
          <p className="mt-1 text-xs text-zinc-500">
            Leave a day blank to use your <strong>base</strong> calorie target. Example: Mon/Wed 2600, Tue/Thu 2800, Sat 2400, Sun 2200.
          </p>
          {useWeekly ? (
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {WEEKDAY_KEYS.map((k) => (
                <label key={k} className="text-xs text-zinc-500">
                  {WEEKDAY_LABELS[k]} (kcal)
                  <input
                    value={weeklyInputs[k]}
                    onChange={(e) => setWeeklyInputs((w) => ({ ...w, [k]: e.target.value }))}
                    placeholder="optional"
                    className="mt-1 w-full rounded-xl border border-white/10 bg-zinc-900 px-3 py-2 text-sm text-white"
                  />
                </label>
              ))}
            </div>
          ) : null}
          {weeklyPreview && weeklyPreview.length > 0 ? (
            <ul className="mt-3 text-xs text-zinc-400">
              {weeklyPreview.map((x) => (
                <li key={x.k}>
                  {x.label}: {x.v} kcal
                </li>
              ))}
            </ul>
          ) : null}
        </GlassCard>

        <div className="lg:col-span-2">
          <button
            type="button"
            onClick={() => save().catch(() => setMsg('Save failed'))}
            className={btnPrimary}
          >
            Save profile
          </button>
        </div>
      </div>
    </PremiumPage>
  );
}
